import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSubscription,
  endSubscription,
  getCatalogue,
  saveCatalogueItem,
} from '../../api/catalogue';
import { useEnvironment } from '../useEnvironment/useEnvironment';
import type { ICatalogueItem, ISubscriptionInput } from '../../types';

export const useCatalogue = () => {
  const { environment } = useEnvironment();
  return useQuery({
    queryKey: ['catalogue', environment],
    queryFn: () => getCatalogue(environment),
    select: ({ data }) => data,
  });
};

export const useSaveCatalogueItem = () => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (item: ICatalogueItem) => saveCatalogueItem(environment, item),
    onSuccess: () => client.invalidateQueries({ queryKey: ['catalogue', environment] }),
  });
};

const invalidateTenant = (
  client: ReturnType<typeof useQueryClient>,
  environment: string,
  tenant: string,
) => {
  client.invalidateQueries({ queryKey: ['tenant-subscriptions', environment, tenant] });
  client.invalidateQueries({ queryKey: ['tenant', environment, tenant] });
  client.invalidateQueries({ queryKey: ['tenants', environment] });
  client.invalidateQueries({ queryKey: ['catalogue', environment] });
  client.invalidateQueries({ queryKey: ['metric', environment] });
};

export const useCreateSubscription = (tenant: string) => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ISubscriptionInput) => createSubscription(environment, tenant, input),
    onSuccess: () => invalidateTenant(client, environment, tenant),
  });
};

export const useEndSubscription = (tenant: string) => {
  const { environment } = useEnvironment();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, endsOn, reason }: { id: string; endsOn: string; reason: string | null }) =>
      endSubscription(environment, tenant, id, endsOn, reason),
    onSuccess: () => invalidateTenant(client, environment, tenant),
  });
};
